<template>
  <b-card>
    <webhook-filter :values="filterValues" :filter-submitting="isLoading" @filter="setFilter" />

    <vue-good-table
      mode="remote"
      :sort-options="{ enabled: false }"
      :is-loading="isLoading"
      :show-footer="true"
      :rows="rows"
      :columns="getColumns"
    >
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'status'">
          <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
            {{ props.row.status ? t('Active') : t('Passive') }}
          </b-badge>
        </span>
        <div v-else-if="props.column.field === 'description'">
          <div class="font-weight-bold">
            {{ props.row.description }}
          </div>
          <div class="font-small-3">
            {{ props.row.endpoint }}
          </div>
        </div>
        <div v-else-if="props.column.field === 'subscribedEventText'">
          <span>{{ $t(props.row.subscribedEventText) }}</span>
        </div>
        <span v-else-if="props.column.field === 'actions'">
          <span class="d-flex justify-content-center table-icon-buttons">
            <b-button
              :id="`edit-button-${props.row.id}`"
              variant="primary"
              type="button"
              size="sm"
              @click="editWebhook(props.row.id)"
            >
              <feather-icon icon="Edit3Icon" size="15" />
              <b-tooltip
                triggers="hover"
                :target="`edit-button-${props.row.id}`"
                :title="$t('Edit Webhook')"
                :delay="{ show: 0, hide: 0 }"
                boundary="viewport"
              />
            </b-button>

            <b-button
              :id="`delete-button-${props.row.id}`"
              variant="danger"
              type="button"
              size="sm"
              @click="deleteWebhook(props.row.id)"
            >
              <feather-icon icon="XIcon" size="15" />
              <b-tooltip
                triggers="hover"
                :target="`delete-button-${props.row.id}`"
                :title="$t('Delete Webhook')"
                :delay="{ show: 0, hide: 0 }"
                boundary="viewport"
              />
            </b-button>
          </span>
        </span>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import { BBadge, BCard, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import WebhookFilter from '@/views/pages/web-hooks/WebhookFilter';
import useService from '@/auth/service/useService';
import { subscribeTypes } from '@/constants/webhooks.constants';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    WebhookFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      filter: {},
      filterValues: {},
      subscribeTypes
    };
  },
  computed: {
    ...mapGetters({}),
    getColumns() {
      return [
        {
          label: this.$t('Description'),
          field: 'description'
        },
        {
          label: this.$t('Subscribed Event'),
          field: 'subscribedEventText'
        },
        {
          label: this.$t('Data Type'),
          field: 'dataType'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      this.isLoading = true;
      useService.webhookService
        .getAllWebhooks(requestData)
        .then(response => {
          this.rows = response.data.map(item => {
            const subscribedEvent = this.subscribeTypes.find(i => i.value === item.subscribedEvent);
            return {
              ...item,
              subscribedEventText: subscribedEvent ? subscribedEvent.text : item.subscribedEvent
            };
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      this.filterValues = data;
      this.filter.page = 1;
      this.loadItems();
    },
    editWebhook(webhook_id) {
      this.$router.push({ name: 'edit_web_hook', params: { webhook_id } });
    },
    deleteWebhook(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Webhook Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.webhookService.deleteWebhook(id).then(response => {
            this.showSuccessMessage(
              `${response.message || this.$t('Delete Webhook Success Message')}`
            );

            const index = this.rows.findIndex(item => item.id === id);
            this.rows.splice(index, 1);
          });
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
